
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import './ProviderDetail.css';
import NavBarTop from '../components/NavBarTop';

const ProviderDetail = () => {
    const { id } = useParams();
    const [provider, setProvider] = useState(null);
    const [ad, setAd] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showContactModal, setShowContactModal] = useState(false);
    const [leadData, setLeadData] = useState({ name: '', mobile: '', place: '' });
    const [leadContext, setLeadContext] = useState(null); // { type: 'call'|'whatsapp'|'email', value: '' }
    const [leadSubmitted, setLeadSubmitted] = useState(false);

    useEffect(() => {
        const fetchProvider = async () => {
            try {
                const response = await axios.get(`https://physiodirectory.in/api/provider/${id}`);
                setProvider(response.data.provider);
                if (response.data.ad) {
                    setAd(response.data.ad);
                }
                setLoading(false);
            } catch (err) {
                console.error("Error fetching provider details:", err);
                setError("Failed to load provider details.");
                setLoading(false);
            }
        };

        fetchProvider();
    }, [id]);

    const hasFeature = (featureName) => {
        if (!provider || !provider.active_features) return false;
        return provider.active_features.includes(featureName);
    };

    const handleLeadSubmit = async (e) => {
        e.preventDefault();
        try {
            await axios.post(`https://physiodirectory.in/providers/${id}/leads`, {
                name: leadData.name,
                mobile: leadData.mobile,
                place: leadData.place,
                source: leadContext.type
            });
            setShowContactModal(false);
            // Use native handler after submit
            const { type, value } = leadContext;
            if (type === 'call') {
                if (window.Native && window.Native.openExternal) {
                    window.Native.openExternal({ url: `tel:${value}` });
                } else {
                    window.location.href = `tel:${value}`;
                }
            } else if (type === 'whatsapp') {
                const cleanNumber = value.replace(/[^0-9]/g, '');
                const url = `https://wa.me/${cleanNumber}`;
                if (window.Native && window.Native.openExternal) {
                    window.Native.openExternal({ url });
                } else {
                    window.location.href = url;
                }
            } else if (type === 'email') {
                if (window.Native && window.Native.openExternal) {
                    window.Native.openExternal({ url: `mailto:${value}` });
                } else {
                    window.location.href = `mailto:${value}`;
                }
            } else if (type === 'website' || type === 'location') {
                if (window.Native && window.Native.openExternal) {
                    window.Native.openExternal({ url: value });
                } else {
                    window.open(value, '_blank');
                }
            }
        } catch (error) {
            console.error("Error submitting lead:", error);
            alert("Failed to submit your details. Please try again.");
        }
    };

    const openLeadModal = (type, value) => {
        // Always show modal for contact actions
        setLeadContext({ type, value });
        setShowContactModal(true);
    };

    if (loading) return <div className="text-center p-5"><div className="spinner-border text-primary" role="status"></div></div>;
    if (error) return <div className="alert alert-danger m-3">{error}</div>;
    if (!provider) return <div className="text-center p-5">Provider not found</div>;

    const role = provider.role;
    let profile = {};
    if (role === 'physiotherapist') profile = provider.physiotherapist_profile || {};
    else if (role === 'clinic') profile = provider.clinic_profile || {};
    else if (role === 'rehab_center') profile = provider.rehab_center_profile || {};

    // Helper to parse qualifications safely
    const parseList = (data) => {
        if (!data) return [];
        if (Array.isArray(data)) return data;
        try {
            const parsed = JSON.parse(data);
            return Array.isArray(parsed) ? parsed : [data];
        } catch (e) {
            return [data];
        }
    };

    // Name formatting logic
    let tempName = provider.name;
    let displayName = tempName;
    if (role === 'physiotherapist') {
        const prefix = profile.prefix ? profile.prefix : '';
        // Clean existing name
        tempName = tempName.replace(/\sPT$/i, ''); 
        tempName = tempName.replace(/^Dr\.?\s*/i, '');
        displayName = `${prefix ? prefix + ' ' : ''}${tempName} (PT)`;
    } else {
         displayName = tempName.replace(/^Dr\.?\s*/i, '').replace(/\sPT$/i, '');
    }

    // Image Logic
    const defaultBanner = 'https://physiodirectory.in/assets/images/pd_bgm.png';
    const bannerUrl = (hasFeature('Banner Customization') && provider.banner_url) ? provider.banner_url : defaultBanner;
    
    let defaultProfilePic = 'https://physiodirectory.in/assets/images/pd_image.png'; // PT default
    if (role === 'clinic') defaultProfilePic = 'https://physiodirectory.in/assets/images/clinic.png';
    if (role === 'rehab_center') defaultProfilePic = 'https://physiodirectory.in/assets/images/rahab.png';
    
    const profilePicUrl = (hasFeature('Profile Photo') && provider.profile_photo_url) ? provider.profile_photo_url : defaultProfilePic;


    return (
        <>
            <NavBarTop title="Profile Details" />
            <div style={{ paddingBottom: showContactModal ? '160px' : '0' }}>
                <div className="provider-detail-container">
            {/* Header / Cover */}
            <div className="profile-header-card container-fluid px-0">
                <div 
                    className="cover-photo-wrapper"
                    style={{ backgroundImage: `url(${bannerUrl})` }}
                >
                    <div className="cover-overlay"></div>
                </div>

                <div className="profile-header-content container">
                    <div className="d-flex flex-column align-items-center flex-md-row align-items-md-end text-md-start">
                        <div className="profile-avatar-container">
                            <img 
                                src={profilePicUrl} 
                                alt={provider.name} 
                                className="profile-avatar-img"
                                onError={(e) => {
                                    if(e.target.src !== defaultProfilePic) {
                                        e.target.onerror = null; 
                                        e.target.src=defaultProfilePic;
                                    }
                                }} 
                            />
                        </div>
                        <div className="ms-md-3 mb-3 text-center text-md-start flex-grow-1">
                             {/* Name Logic */}
                            {((role === 'physiotherapist' && hasFeature('Name')) || 
                              (role === 'clinic' && hasFeature('Clinic Name')) || 
                              (role === 'rehab_center' && hasFeature('Rehab Centre Name'))) ? (
                                <div className="d-flex align-items-center justify-content-center justify-content-md-start flex-wrap gap-2 mb-1">
                                    <h1 className="provider-name mb-0">{displayName}</h1>
                                    {(String(provider.is_approved) === '1' || provider.is_approved === true) && (
                                        <span className="badge bg-success-subtle text-success px-2 py-1 small rounded-pill d-flex align-items-center" style={{fontSize: '0.7rem'}}>
                                            <i className="bi bi-check-circle-fill me-1"></i>Verified
                                        </span>
                                    )}
                                </div>
                            ) : (
                                <span className="text-muted">Name Hidden</span>
                            )}
                            
                            {/* Role Badge */}
                            <div className="mb-1">
                                <span className="badge bg-primary-subtle text-primary px-2 py-1 fw-semibold small">
                                    {role === 'rehab_centre' ? 'Rehab Centre' : (role === 'clinic' ? 'Clinic' : 'Physiotherapist')}
                                </span>
                                <span className="text-muted small ms-2">
                                    ID: {(() => {
                                        let displayId = provider.profile_id;
                                        if (!displayId) {
                                            if (role === 'physiotherapist' && provider.physiotherapist_profile) displayId = provider.physiotherapist_profile.profile_id;
                                            else if (role === 'clinic' && provider.clinic_profile) displayId = provider.clinic_profile.profile_id;
                                            else if (role === 'rehab_center' && provider.rehab_center_profile) displayId = provider.rehab_center_profile.profile_id;
                                        }
                                        if (!displayId && provider.id) {
                                            displayId = `PHY00${provider.id}`;
                                        } else if (displayId && !isNaN(displayId)) {
                                            displayId = `PHY00${displayId}`;
                                        }
                                        if (displayId && String(displayId).startsWith('PHY') && !String(displayId).startsWith('PHY00')) {
                                            // If starts with PHY but not PHY00, leave as is
                                        } else if (displayId && !String(displayId).startsWith('PHY') && !String(displayId).startsWith('#')) {
                                            displayId = `PHY00${displayId}`;
                                        }
                                        return displayId;
                                    })()}
                                </span>
                            </div>

                            {/* Address Construction Logic */}
                            {(() => {
                                let fullAddress = '';
                                const parts = [];
                                
                                if (role === 'physiotherapist') {
                                    if (profile.address_line_1) parts.push(profile.address_line_1);
                                    if (profile.address_line_2) parts.push(profile.address_line_2);
                                    if (profile.address_line_3) parts.push(profile.address_line_3);
                                    if (profile.address_line_4) parts.push(profile.address_line_4);
                                    if (profile.pin_code) parts.push(profile.pin_code);
                                    if (parts.length === 0 && profile.address) fullAddress = profile.address;
                                } else {
                                    if (profile.doctor_address_line_1) parts.push(profile.doctor_address_line_1);
                                    if (profile.doctor_address_line_2) parts.push(profile.doctor_address_line_2);
                                    if (profile.doctor_address_line_3) parts.push(profile.doctor_address_line_3);
                                    if (profile.doctor_address_line_4) parts.push(profile.doctor_address_line_4);
                                    if (profile.doctor_pin_code) parts.push(profile.doctor_pin_code);
                                    if (parts.length === 0 && profile.doctor_address) fullAddress = profile.doctor_address;
                                }
                                
                                fullAddress = parts.length > 0 ? parts.join(', ') : fullAddress;

                                if (fullAddress && hasFeature('Address')) {
                                    return <div className="text-muted small mt-1"><i className="bi bi-geo-alt-fill me-1"></i>{fullAddress}</div>;
                                }
                                return null;
                            })()}
                        </div>
                    </div>
                </div>
            </div>

            <div className="container">
                <div className="row">
                    <div className="col-lg-8">
                        {/* Profile Details Card */}
                        <div className="section-card">
                            <div className="section-header">
                                <h5 className="section-title"><i className="bi bi-info-circle me-2 text-primary"></i>About</h5>
                            </div>
                            <div className="section-body">
                                {/* Conditional Rendering based on Features */}
                                {role === 'physiotherapist' && (
                                    <>
                                        {/* Reg Number */}
                                        {profile.council_registration_number && (
                                            <div className="info-item">
                                                <div className="info-label">Reg. Number</div>
                                                <div className="info-value">{profile.council_registration_number}</div>
                                            </div>
                                        )}

                                        {/* Age, Nationality, DOB, Gender grid similar to web */}
                                        <div className="row g-2 mb-2">
                                            {profile.nationality && hasFeature('Education Details') && (
                                                 <div className="col-6 col-md-3">
                                                    <div className="info-item flex-column align-items-start">
                                                        <div className="info-label" style={{minWidth:'auto'}}>Nationality</div>
                                                        <div className="info-value text-capitalize">{profile.nationality}</div>
                                                    </div>
                                                </div>
                                            )}
                                            {profile.dob && hasFeature('Education Details') && (
                                                 <div className="col-6 col-md-4">
                                                    <div className="info-item flex-column align-items-start">
                                                        <div className="info-label" style={{minWidth:'auto'}}>Date of Birth</div>
                                                        <div className="info-value">{new Date(profile.dob).toLocaleDateString('en-GB', {day: 'numeric', month: 'short', year: 'numeric'})}</div>
                                                    </div>
                                                </div>
                                            )}
                                            {profile.age && profile.age != 0 && hasFeature('Education Details') ? (
                                                 <div className="col-6 col-md-2">
                                                    <div className="info-item flex-column align-items-start">
                                                        <div className="info-label" style={{minWidth:'auto'}}>Age</div>
                                                        <div className="info-value">{parseInt(profile.age)} years</div>
                                                    </div>
                                                </div>
                                            ) : null}
                                            {profile.gender && hasFeature('Education Details') && (
                                                <div className="col-6 col-md-3">
                                                    <div className="info-item flex-column align-items-start">
                                                        <div className="info-label" style={{minWidth:'auto'}}>Gender</div>
                                                        <div className="info-value text-capitalize">{profile.gender}</div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        {/* Qualifications Detailed List */}
                                        {profile.qualification && hasFeature('Education Details') && (
                                            <div className="info-item info-item-full mt-2">
                                                <div className="info-label">Qualifications</div>
                                                <div className="info-value">
                                                     <ul className="ps-3 mb-0">
                                                        {parseList(profile.qualification).map((q, i) => (
                                                            <li key={i}>
                                                                <strong>{q}</strong>
                                                                {/* Logic to show BPT/MPT year details if available */}
                                                                {q === 'BPT' && (profile.bpt_year || profile.bpt_month) && 
                                                                    <span className="text-muted small ms-1">({profile.bpt_month ? new Date(0, parseInt(profile.bpt_month)-1).toLocaleString('default', {month:'long'}) + ' ': ''}{profile.bpt_year})</span>
                                                                }
                                                                {q === 'MPT' && (profile.mpt_year || profile.mpt_month) && 
                                                                    <span className="text-muted small ms-1">({profile.mpt_month ? new Date(0, parseInt(profile.mpt_month)-1).toLocaleString('default', {month:'long'}) + ' ': ''}{profile.mpt_year})</span>
                                                                }
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            </div>
                                        )}

                                        {/* Additional Qualifications */}
                                        {(() => {
                                            const addQuals = [...parseList(profile.additional_qualifications), ...parseList(profile.additional_qualification)]
                                                            .filter(x => x && x !== '0' && x !== 0 && x.toString().trim() !== '');
                                            
                                            if (addQuals.length === 0 || !hasFeature('Education Details')) return null;

                                            return (
                                                <div className="info-item info-item-full mt-2">
                                                    <div className="info-label">Additional Qualifications</div>
                                                    <div className="info-value">
                                                         <ul className="ps-3 mb-0">
                                                            {addQuals.map((q, i) => (
                                                                <li key={i}>{q}</li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                </div>
                                            );
                                        })()}

                                        {/* Experience Years - Uses 'Working Institution Details' flag per web */}
                                        {profile.years_of_experience && profile.years_of_experience != 0 && String(profile.years_of_experience) !== '0' && hasFeature('Working Institution Details') ? (
                                            <div className="info-item mt-3">
                                                <div className="info-label">Total Experience</div>
                                                <div className="info-value">{profile.years_of_experience} years</div>
                                            </div>
                                        ) : null}

                                        {/* Experience Details List - Uses 'Working Institution Details' flag per web */}
                                        {profile.experience && hasFeature('Working Institution Details') && parseList(profile.experience).length > 0 && (
                                             <div className="info-item info-item-full mt-2">
                                                <div className="info-label">Experience Details</div>
                                                <div className="info-value">
                                                    {parseList(profile.experience).map((exp, i) => (
                                                        <div key={i} className="mb-2 pb-2 border-bottom">
                                                            <div className="fw-bold">{typeof exp === 'object' ? exp.institution : exp}</div>
                                                            {typeof exp === 'object' && (exp.start_date || exp.end_date) && (
                                                                <div className="text-muted small">
                                                                    {exp.start_date ? new Date(exp.start_date).toLocaleString('default', {month:'short', year:'numeric'}) : ''}
                                                                    {exp.end_date ? ' - ' + new Date(exp.end_date).toLocaleString('default', {month:'short', year:'numeric'}) : (exp.start_date ? ' - Present' : '')}
                                                                </div>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* Memberships - Uses 'Working Institution Details' flag per web */}
                                        {profile.membership_details && hasFeature('Working Institution Details') && parseList(profile.membership_details).length > 0 && (
                                            <div className="info-item info-item-full mt-3">
                                                <div className="info-label">Memberships</div>
                                                <div className="info-value">
                                                    {parseList(profile.membership_details).map((membership, i) => (
                                                        <div key={i} className="mb-3 pb-3 border-bottom">
                                                            {typeof membership === 'object' ? (
                                                                <>
                                                                    {membership.association && (
                                                                        <div className="mb-1">
                                                                            <strong className="text-muted small d-inline-block" style={{minWidth:'90px'}}>Association:</strong>
                                                                            <span className="small">{membership.association}</span>
                                                                        </div>
                                                                    )}
                                                                    {membership.number && (
                                                                        <div className="mb-1">
                                                                            <strong className="text-muted small d-inline-block" style={{minWidth:'90px'}}>Reg. No:</strong>
                                                                            <span className="small">{membership.number}</span>
                                                                        </div>
                                                                    )}
                                                                    {membership.validity && (
                                                                        <div className="mb-1">
                                                                            <strong className="text-muted small d-inline-block" style={{minWidth:'90px'}}>Validity:</strong>
                                                                            <span className="small">{new Date(membership.validity).toLocaleDateString('en-GB', {day: 'numeric', month: 'short', year: 'numeric'})}</span>
                                                                        </div>
                                                                    )}
                                                                    {membership.type && (
                                                                        <div className="mb-1">
                                                                            <strong className="text-muted small d-inline-block" style={{minWidth:'90px'}}>Type:</strong>
                                                                            <span className="small">{membership.type}</span>
                                                                        </div>
                                                                    )}
                                                                </>
                                                            ) : (
                                                                <div className="mb-2">{membership}</div>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* Address removed from About section per user request */}
                                        {/* 
                                        {profile.address && hasFeature('Address') && (
                                            <div className="info-item mt-3">
                                                <div className="info-label">Address</div>
                                                <div className="info-value">{profile.address}</div>
                                            </div>
                                        )}
                                        */}
                                        {profile.experience_description && (
                                            <div className="info-item info-item-full mt-3">
                                                <div className="info-label">About Me</div>
                                                <div className="info-value" style={{whiteSpace:'pre-line'}}>{profile.experience_description}</div>
                                            </div>
                                        )}
                                    </>
                                )}

                                {(role === 'clinic' || role === 'rehab_center') && (
                                    <>
                                        {/* Clinic Reg No */}
                                        {(profile.clinic_registration_number || profile.registration_number) && hasFeature('Organization Details') && (
                                             <div className="info-item">
                                                <div className="info-label">{role === 'clinic' ? 'Clinic Reg No' : 'RHB Reg No'}</div>
                                                <div className="info-value">{profile.clinic_registration_number || profile.registration_number}</div>
                                             </div>
                                        )}
                                        {/* Established Date */}
                                        {profile.established_date && hasFeature('Organization Details') && (
                                             <div className="info-item">
                                                <div className="info-label">Established</div>
                                                <div className="info-value">{new Date(profile.established_date).toLocaleDateString('en-GB', {day: 'numeric', month: 'short', year: 'numeric'})}</div>
                                             </div>
                                        )}
                                        {/* Address removed from About section per user request */}
                                        {/*
                                        {profile.doctor_address && hasFeature('Address') && (
                                            <div className="info-item">
                                                <div className="info-label">Address</div>
                                                <div className="info-value">{profile.doctor_address}</div>
                                            </div>
                                        )}
                                        */}

                                        {/* Owner Details */}
                                        {profile.clinic_owner_name && hasFeature('Owner Details') && (
                                            <div className="info-item">
                                                <div className="info-label">Owner</div>
                                                <div className="info-value">
                                                    {(profile.clinic_owner_prefix || '') + ' ' + (typeof profile.clinic_owner_name === 'string' ? profile.clinic_owner_name : profile.clinic_owner_name.join(', '))} (PT)
                                                </div>
                                            </div>
                                        )}
                                        {profile.owner_name && hasFeature('Owner Details') && (
                                            <div className="info-item">
                                                <div className="info-label">Owner</div>
                                                <div className="info-value">
                                                    {(profile.owner_prefix || '') + ' ' + (typeof profile.owner_name === 'string' ? profile.owner_name : profile.owner_name.join(', '))} (PT)
                                                </div>
                                            </div>
                                        )}

                                        {/* Working Physios */}
                                        {((profile.working_physios_male_count > 0 || profile.working_physios_female_count > 0 || profile.number_of_working_physios > 0) && hasFeature('Working Physios Count')) && (
                                            <div className="info-item">
                                                <div className="info-label">Working Physios</div>
                                                <div className="info-value">
                                                    {(parseInt(profile.working_physios_male_count || 0) + parseInt(profile.working_physios_female_count || 0)) || profile.number_of_working_physios}
                                                    {(profile.working_physios_male_count > 0 || profile.working_physios_female_count > 0) && 
                                                        <span className="text-muted small ms-1">({profile.working_physios_male_count || 0} Male, {profile.working_physios_female_count || 0} Female)</span>
                                                    }
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}

                                {/* Common Education & Experience (Simplified for brevity but adhering to logic) */}
                                {hasFeature('Education Details') && (
                                    <>
                                        {/* Removed provider.full_qualification duplicate as per request to show only in About list */}
                                        {(role === 'clinic' || role === 'rehab_center') && profile.doctor_qualification && (
                                            <div className="info-item info-item-full mt-3">
                                                <div className="info-label">Owner Qualification</div>
                                                <div className="info-value">
                                                    <ul className="ps-3 mb-0">
                                                        {parseList(profile.doctor_qualification).map((q, i) => <li key={i}>{q}</li>)}
                                                    </ul>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}

                                {hasFeature('Experience') && profile.experience_years && (
                                    <div className="info-item mt-2">
                                        <div className="info-label">Experience</div>
                                        <div className="info-value">{profile.experience_years} Years</div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Services */}
                        {((provider.services && provider.services.length > 0) || (profile.services_offered)) && hasFeature('Services Offered') && (
                            <div className="section-card">
                                <div className="section-header">
                                    <h5 className="section-title"><i className="bi bi-list-check me-2 text-primary"></i>Services Offered</h5>
                                </div>
                                <div className="section-body">
                                    <div className="row g-0">
                                        {parseList(profile.services_offered).map((service, i) => (
                                            <div className="col-md-6" key={`p-${i}`}>
                                                <div className="service-item">
                                                    <i className="bi bi-check-circle-fill"></i>
                                                    <span>{typeof service === 'string' ? service : service.toString()}</span>
                                                </div>
                                            </div>
                                        ))}
                                        {provider.services && provider.services.map((service, i) => (
                                            <div className="col-md-6" key={`s-${i}`}>
                                                <div className="service-item">
                                                    <i className="bi bi-check-circle-fill"></i>
                                                    <span>{service.name}</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}
                        
                        {/* More sections like Facilities, Associated Physios can be added similarly */}

                    </div>

                    <div className="col-lg-4">
                        {/* Availability */}
                        {hasFeature('Time Availability') && (
                            <div className="section-card mb-3">
                                <div className="section-header">
                                    <h5 className="section-title"><i className="bi bi-calendar-check me-2 text-primary"></i>Availability</h5>
                                </div>
                                <div className="section-body" style={{padding: '0.6rem 0'}}>
                                    <div className="table-responsive">
                                        <table className="table availability-table mb-0">
                                            <thead>
                                                <tr>
                                                    <th style={{width: '35%'}}>Day</th>
                                                    <th>Time</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(day => {
                                                    const avail = provider.availabilities ? provider.availabilities.find(a => a.day === day) : null;
                                                    return (
                                                        <tr key={day}>
                                                            <td className="fw-semibold" style={{fontSize: '0.85rem'}}>{day}</td>
                                                            <td>
                                                                {(avail && avail.is_available) ? (
                                                                    <span className="text-dark" style={{fontSize: '0.85rem'}}>
                                                                        {/* Format time manually or utilizing Date, assuming 'HH:mm:ss' from DB */}
                                                                        {(() => {
                                                                            try {
                                                                                const d1 = new Date('1970-01-01T' + avail.start_time);
                                                                                const d2 = new Date('1970-01-01T' + avail.end_time);
                                                                                return d1.toLocaleTimeString([], {hour: 'numeric', minute:'2-digit', hour12: true}) + ' - ' + d2.toLocaleTimeString([], {hour: 'numeric', minute:'2-digit', hour12: true});
                                                                            } catch(e) { return avail.start_time + ' - ' + avail.end_time }
                                                                        })()}
                                                                    </span>
                                                                ) : (
                                                                    <span className="text-muted" style={{fontSize: '0.85rem'}}>Not Available</span>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Contact Actions */}
                        <div className="card contact-card mb-0">
                            <div className="contact-header">
                                <h5 className="mb-0 fw-bold" style={{fontSize: '1rem'}}><i className="bi bi-telephone me-2"></i>Contact Information</h5>
                            </div>
                            <div className="card-body p-4">
                                {hasFeature('Consultation Fee') && profile.consultation_fees && (
                                    <div className="mb-3 pb-3 border-bottom">
                                        <div className="info-label mb-1" style={{fontSize: '0.7rem'}}>Consultation Fee</div>
                                        <div className="info-value small">
                                            <i className="bi bi-currency-rupee me-1"></i>{Number(profile.consultation_fees).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </div>
                                    </div>
                                )}
                                <div className="d-grid gap-2">
                                    {provider.mobile_number && hasFeature('Call Button') && (
                                        <button className="btn btn-primary rounded-pill fw-semibold py-2" onClick={() => openLeadModal('call', provider.mobile_number)}>
                                            <i className="bi bi-telephone-fill me-2"></i>Call Now
                                        </button>
                                    )}
                                    {provider.whatsapp_number && hasFeature('Whatsapp Button') && (
                                        <button className="btn btn-success rounded-pill fw-semibold py-2" onClick={() => openLeadModal('whatsapp', provider.whatsapp_number)}>
                                            <i className="bi bi-whatsapp me-2"></i>WhatsApp
                                        </button>
                                    )}
                                    {provider.email && hasFeature('Email') && (
                                        <button className="btn btn-secondary rounded-pill fw-semibold py-2" onClick={() => openLeadModal('email', provider.email)}>
                                            <i className="bi bi-envelope-fill me-2"></i>Email
                                        </button>
                                    )}
                                    {profile.website && hasFeature('Website') && (
                                        <button className="btn btn-info text-white rounded-pill fw-semibold py-2" onClick={() => openLeadModal('website', profile.website)}>
                                            <i className="bi bi-globe me-2"></i>Website
                                        </button>
                                    )}
                                    {profile.google_map_url && hasFeature('Google Location') && (
                                        <button className="btn btn-primary rounded-pill fw-semibold py-2" onClick={() => openLeadModal('location', profile.google_map_url)}>
                                            <i className="bi bi-geo-alt-fill me-2"></i>Direction
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Advertisement */}
                        {ad && ad.image_url && (
                            <div className="card border-0 shadow-sm mt-2" style={{borderRadius: '12px', overflow: 'hidden'}}>
                                <div className="position-absolute top-0 end-0 m-2" style={{zIndex: 10}}>
                                    <span className="badge bg-secondary small" style={{fontSize: '0.65rem', padding: '0.25rem 0.5rem'}}>Ad</span>
                                </div>
                                <a href={ad.link_url || '#'} target="_blank" rel="noreferrer" className="d-block">
                                    <img src={ad.image_url} alt={ad.title || 'Advertisement'} className="img-fluid w-100" />
                                </a>
                                {ad.title && (
                                    <div className="p-2 bg-white">
                                        <div className="fw-bold small text-truncate">{ad.title}</div>
                                        {ad.description && <div className="text-muted small text-truncate">{ad.description}</div>}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Mock Lead Modal */}
            {showContactModal && (
                <div className="modal show d-block" style={{backgroundColor: 'rgba(0,0,0,0.5)', position: 'fixed', left: 0, right: 0, bottom: 0, top: 0, zIndex: 9999}} tabIndex="-1">
                    <div className="modal-dialog" style={{position: 'fixed', left: 0, right: 0, bottom: '60px', margin: 0, maxWidth: '100vw'}}>
                            <div className="modal-content rounded-top-4 border-0 shadow-lg p-0 bottom-sheet-modal-content" style={{maxWidth: 420, margin: '0 auto', borderRadius: '24px 24px 0 0', background: '#fff', minHeight: 320, paddingBottom: '0'}}>
                                <div className="d-flex align-items-center justify-content-between px-3 pt-3 pb-2 border-0" style={{borderBottom: 'none'}}>
                                <button type="button" className="btn btn-light btn-sm rounded-circle shadow-none me-2" style={{position: 'absolute', left: 16, top: 16, zIndex: 2}} onClick={() => { setShowContactModal(false); setLeadSubmitted(false); }}>
                                    <i className="bi bi-x-lg"></i>
                                </button>
                                <h5 className="modal-title fw-bold w-100 text-center" style={{fontSize: '1.1rem', margin: 0}}>Contact Provider</h5>
                            </div>
                            <div className="modal-body pt-2 pb-1 px-3" style={{background: '#f8f9fc', borderRadius: '1rem 1rem 0 0'}}>
                                {leadSubmitted ? (
                                    <div className="text-center py-4">
                                        <h5 className="mb-3">Contact Information</h5>
                                        <p className="lead fw-bold text-primary mb-4">{leadContext?.value}</p>
                                        <div className="d-grid">
                                            {leadContext?.type === 'call' && <a href={`tel:${leadContext.value}`} className="btn btn-success btn-lg"><i className="bi bi-telephone-fill me-2"></i> Call Now</a>}
                                            {leadContext?.type === 'whatsapp' && <a href={`https://wa.me/${leadContext.value.replace(/[^0-9]/g, '')}`} target="_blank" rel="noreferrer" className="btn btn-success btn-lg"><i className="bi bi-whatsapp me-2"></i> Open WhatsApp</a>}
                                            {leadContext?.type === 'email' && <a href={`mailto:${leadContext.value}`} className="btn btn-primary btn-lg"><i className="bi bi-envelope-fill me-2"></i> Send Email</a>}
                                            {(leadContext?.type === 'website' || leadContext?.type === 'location') && <a href={leadContext.value} target="_blank" rel="noreferrer" className="btn btn-primary btn-lg"><i className="bi bi-box-arrow-up-right me-2"></i> Open Link</a>}
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        <p className="text-muted small">Enter your details to view contact information.</p>
                                        <form className="bottom-sheet-modal-form" onSubmit={handleLeadSubmit} autoComplete="off" style={{ marginBottom: '64px' }}>
                                            <div className="mb-3">
                                                <label className="form-label fw-semibold small mb-1">Name</label>
                                                <input type="text" className="form-control rounded-pill px-3 py-2 border-0 app-input" required 
                                                    value={leadData.name} onChange={e => setLeadData({...leadData, name: e.target.value})} />
                                            </div>
                                            <div className="mb-3">
                                                <label className="form-label fw-semibold small mb-1">Mobile</label>
                                                <input type="tel" className="form-control rounded-pill px-3 py-2 border-0 app-input" required 
                                                    value={leadData.mobile} onChange={e => setLeadData({...leadData, mobile: e.target.value})} />
                                            </div>
                                            <div style={{marginBottom: '2px', paddingBottom: 0}}>
                                                <label className="form-label fw-semibold small mb-1" style={{marginBottom: '2px'}}>City</label>
                                                <input type="text" className="form-control rounded-pill px-3 py-2 border-0 app-input" style={{marginBottom: '0', paddingBottom: '6px'}}
                                                    value={leadData.place} onChange={e => setLeadData({...leadData, place: e.target.value})} />
                                            </div>
                                            <div className="bottom-sheet-modal-btnbar" style={{borderTop: 'none', marginTop: '-4px'}}>
                                                <button type="submit" className="btn btn-primary rounded-pill fw-bold py-2 px-4">Submit & Continue</button>
                                            </div>
                                        </form>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
                </div>
            </div>
        </>
    );
};

export default ProviderDetail;
